import { Box, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';

import { Typography, LoadingButton, ScreenLoaderBackdrop, SizingMultiplier } from '@ocx/ui';
import { OtpChannel } from '@ocx/graphql';
import { SignupSupportLink } from '@ocx/feat-support';
import { formatPhoneNumberForInput, redactPhoneNumber } from '@ocx/utils-phone-number';
import { OTPField } from '@ocx/ui-forms';
import { useConfiguration } from '@ocx/data-configuration';

import { RequestMagicLinkParams, RequestOtpParams } from '../../signup.types';
import { useShowAttemptTimeoutMessage } from '../../use-show-attempt-timeout-message.hook';
import { OTP_SIZE } from '../../signup.constants';
import { messages } from '../../signup.messages';
import { ChangeNumberLink } from '../../components';

type SignupOtpPhoneCallControllerProps = {
  phoneNumber: string;
  isRequestMagicLinkLoading: boolean;
  isRequestOTPLoading: boolean;
  isSubmitOTPLoading: boolean;
  isBlockedByTimeout: boolean;
  isSubmitButtonsDisabled: boolean;
  handleRequestMagicLink(params: RequestMagicLinkParams): Promise<boolean>;
  handleRequestOTP(params: RequestOtpParams): Promise<boolean>;
  handleSubmitOTP(params: { otpValue: string; otpChannel: OtpChannel }): Promise<void>;
};

export const SignupOtpPhoneCallController = (props: SignupOtpPhoneCallControllerProps) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const showAttemptTimeoutMessage = useShowAttemptTimeoutMessage();
  const { channels: otpChannels } = useConfiguration('auth.otp');
  const {
    phoneNumber,
    isRequestMagicLinkLoading,
    isRequestOTPLoading,
    isSubmitOTPLoading,
    isBlockedByTimeout,
    isSubmitButtonsDisabled,
    handleRequestMagicLink,
    handleRequestOTP,
    handleSubmitOTP,
  } = props;

  const [loadingOTPChanel, setLoadingOTPChanel] = useState<OtpChannel | null>(null);
  const handleResendOTPClick = useCallback(
    async (otpChannel: OtpChannel) => {
      if (isBlockedByTimeout) {
        showAttemptTimeoutMessage();
        return;
      }
      setLoadingOTPChanel(otpChannel);
      const res = await handleRequestOTP({ otpChannel, phoneNumber });
      if (res && otpChannel === OtpChannel.PhoneCall) {
        snackbar.open(intl.formatMessage(messages['signup:auth-message-resent']), { variant: 'success' });
      }
    },
    [handleRequestOTP, intl, isBlockedByTimeout, phoneNumber, showAttemptTimeoutMessage, snackbar],
  );

  const handleRequestMagicLinkClick = useCallback(() => {
    if (isBlockedByTimeout) {
      showAttemptTimeoutMessage();
      return;
    }
    handleRequestMagicLink({ phoneNumber });
  }, [handleRequestMagicLink, isBlockedByTimeout, phoneNumber, showAttemptTimeoutMessage]);

  const pageTitleValues = useMemo(() => {
    const displayPhoneNumber = formatPhoneNumberForInput(phoneNumber);

    return {
      phoneNumber: displayPhoneNumber,
      phoneNumberRedacted: redactPhoneNumber(displayPhoneNumber),
    };
  }, [phoneNumber]);

  return (
    <>
      <ScreenLoaderBackdrop open={isSubmitOTPLoading} />
      <Box p={SizingMultiplier.lg}>
        <Stack spacing={SizingMultiplier.sm} alignItems="stretch">
          <Typography variant="h6" align="center" sx={{ textWrap: 'balance' }}>
            <FormattedMessage {...messages['signup:otp-method:phone-call:page-title']} values={pageTitleValues} />
          </Typography>
          <ChangeNumberLink sx={{ mt: -SizingMultiplier.md }} />
        </Stack>
        <Box paddingY={SizingMultiplier.xl}>
          <OTPField
            onSubmit={(otpValue) => handleSubmitOTP({ otpValue, otpChannel: OtpChannel.PhoneCall })}
            valueLength={OTP_SIZE}
            cleanOnSubmitEnabled
            autoSubmitEnabled
            blurOnSubmitEnabled
            autoFocus
            label={intl.formatMessage(messages['signup:otp-method:otp-field-label'])}
          />
        </Box>
        <Stack spacing={SizingMultiplier.md} alignItems="stretch">
          <Typography mt={SizingMultiplier.lg} color="textSecondary" align="center">
            <FormattedMessage {...messages['signup:otp-method:phone-call:didnt-receive-otp-question']} />
          </Typography>
          <LoadingButton
            onClick={() => handleResendOTPClick(OtpChannel.PhoneCall)}
            disabled={isSubmitButtonsDisabled}
            loading={isRequestOTPLoading && loadingOTPChanel === OtpChannel.PhoneCall}>
            <FormattedMessage {...messages['signup:resend-otp:button-text:phone-call']} />
          </LoadingButton>
          {otpChannels.includes(OtpChannel.Sms) && (
            <LoadingButton
              variant="outlined"
              onClick={() => handleResendOTPClick(OtpChannel.Sms)}
              disabled={isSubmitButtonsDisabled}
              loading={isRequestOTPLoading && loadingOTPChanel === OtpChannel.Sms}>
              <FormattedMessage {...messages['signup:send-otp:button-text:sms']} />
            </LoadingButton>
          )}
          <LoadingButton
            variant="outlined"
            onClick={handleRequestMagicLinkClick}
            disabled={isSubmitButtonsDisabled}
            loading={isRequestMagicLinkLoading}>
            <FormattedMessage {...messages['signup:use-magic-link-method:button-text']} />
          </LoadingButton>
          <SignupSupportLink sx={{ mt: -SizingMultiplier.md }} />
        </Stack>
      </Box>
    </>
  );
};
